import { isPaidAddon } from 'restful/tickets/addon.utils';
import type {
  Cart,
  CartItem,
  CartSubItem,
  CouponCode,
  MoneyInfo,
  Order,
  Ticket,
} from 'services/api/restful/generated/nofomoRestApi.schemas';
import {
  formatEventDate,
  mapPageEventToDatalayer,
} from 'services/datalayer/utils';

const calculateDiscount = (
  originalPrice: MoneyInfo | null | undefined,
  discountedPrice: MoneyInfo | null | undefined,
) => {
  if (!originalPrice || !discountedPrice) return 0;
  if (originalPrice.amount === discountedPrice.amount) return 0;

  return originalPrice.amount - discountedPrice.amount;
};

export const mapTicketToDataLayer = (
  item: Pick<Ticket, 'id' | 'name'>,
  event: ReturnType<typeof mapPageEventToDatalayer>,
  {
    originalPrice,
    discountedPrice,
  }: {
    originalPrice: MoneyInfo | undefined;
    discountedPrice?: MoneyInfo | undefined;
  },
) => ({
  itemId: item.id,
  itemName: item.name?.['en'] ?? '',
  itemCategory: event.hostingEventName ?? '',
  itemCategory2: formatEventDate(event.eventDate),
  itemBrand: '',
  itemListName: 'Event detail',
  index: 0,
  discount: calculateDiscount(originalPrice, discountedPrice),
  price: discountedPrice?.amount ?? 0,
  quantity: 1,
});

const mapCartItemToDataLayer = (
  item: CartItem | CartSubItem,
  idx: number = 0,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  itemId: item.catalogueItemId,
  itemName: item.name?.['en'] ?? '',
  itemCategory: event.hostingEventName ?? '',
  itemCategory2: formatEventDate(event.eventDate),
  itemBrand: '',
  itemListName: 'Event detail',
  discount: calculateDiscount(
    item.originalItemPrice ?? undefined,
    item.discountedItemPrice ?? undefined,
  ),
  index: idx,
  price: item.discountedItemPrice?.amount ?? 0,
  quantity: item.quantity,
});

/** Parent item plus paid addon childItems. Uses each child's quantity as returned. */
const mapCartItemWithAddonsToDataLayer = (
  item: CartItem,
  startIndex: number,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => {
  const parent = mapCartItemToDataLayer(item, startIndex, event);
  const addonItems =
    item.childItems
      ?.filter((child) => isPaidAddon(child.ticketType, child.addonType))
      .map((child, addonIndex) =>
        mapCartItemToDataLayer(child, startIndex + 1 + addonIndex, event),
      ) ?? [];

  return [parent, ...addonItems];
};

const mapCartItemsToDataLayer = (
  items: Array<CartItem> | null | undefined,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) =>
  items?.reduce<Array<ReturnType<typeof mapCartItemToDataLayer>>>(
    (acc, item) => {
      acc.push(...mapCartItemWithAddonsToDataLayer(item, acc.length, event));
      return acc;
    },
    [],
  ) ?? [];

export const mapOrderToDatalayer = (
  order: Order,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  currency: 'SAR' as const,
  value: order.totalPrice?.amount ?? 0,
  items: mapCartItemsToDataLayer(order.items, event),
  transactionId: order.id,
  coupon: findCouponCode(order.couponCodes),
  taxAmount: order.vatAmount?.amount ?? 0,
  // Always 0 for now
  shippingAmount: 0,
  paymentType:
    (order.totalPrice?.amount ?? 0) === 0
      ? 'FreeOfCharge'
      : order.paymentMethod,
  shippingTier: 'Online',
});

export const mapCartToDatalayer = (
  cart: Cart,
  event: ReturnType<typeof mapPageEventToDatalayer>,
) => ({
  currency: 'SAR' as const,
  value: cart.totalPrice?.amount ?? 0,
  items: mapCartItemsToDataLayer(cart.items, event),
});

const findCoupon = (coupons: Array<CouponCode>) =>
  coupons.find((coupon) => coupon.couponType === 'RegularCoupon');

export const findValidCouponCode = (coupons: Array<CouponCode>) => {
  const coupon = findCoupon(coupons);
  return (coupon?.discountValue?.amount ?? 0 > 0) ? coupon?.code : undefined;
};

const findCouponCode = (coupons: Array<CouponCode>) =>
  findCoupon(coupons)?.code;
